// @ts-check
/**
 * FF Search Results — Custom element for live search results slider.
 *
 * Uses Shopify Section Rendering API:
 * GET /search?type=product&q={query}&limit={n}&section_id=ff-search-headless
 *
 * Features:
 * - Debounced search after each keystroke (configurable delay)
 * - Replaces slider slides with search results from the headless section
 * - Updates parent overlay state (data-ff-search-state) for CSS-driven panel show/hide
 * - Sets "show more" button href to the full search results page
 */

if (!customElements.get('ff-search-results')) {
  class FfSearchResults extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLElement | null} */
      this.ffSlider = null;
      /** @type {object | null} */
      this.splideInstance = null;
      /** @type {HTMLElement | null} */
      this.overlay = null;
      /** @type {HTMLInputElement | null} */
      this.input = null;
      /** @type {HTMLElement | null} */
      this.showMoreWrapper = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      this.debounceTimer = null;
      /** @type {string} */
      this.currentQuery = '';
    }

    connectedCallback() {
      this.ffSlider = this.querySelector('ff-slider');
      this.overlay = this.closest('[id^="ff-search-overlay-"]');
      this.showMoreWrapper = this.querySelector('.ff-search-results__more');

      if (!this.overlay) return;

      this.initWhenReady();
    }

    initWhenReady() {
      this.input = /** @type {HTMLInputElement | null} */ (
        this.overlay && this.overlay.querySelector('.ff-input__field')
      );

      if (this.input) {
        this.input.addEventListener('input', () => this._onInput());
        this._waitForSplide();
      } else {
        setTimeout(() => this.initWhenReady(), 100);
      }
    }

    _waitForSplide() {
      // @ts-ignore — splide is attached by ff-slider custom element
      if (this.ffSlider && this.ffSlider.splide) {
        // @ts-ignore
        this.splideInstance = this.ffSlider.splide;
      } else {
        setTimeout(() => this._waitForSplide(), 100);
      }
    }

    _onInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);

      const q = (this.input && this.input.value.trim()) || '';

      if (!q) {
        this.currentQuery = '';
        this._setState('');
        return;
      }

      const delay = parseInt(this.dataset.debounce || '300');
      this.debounceTimer = setTimeout(() => this._fetchResults(q), delay);
    }

    /** @param {string} q */
    async _fetchResults(q) {
      if (q === this.currentQuery) return;
      this.currentQuery = q;
      this._setState('loading');

      const sectionId = this.dataset.sectionId || 'ff-search-headless';
      const limit = this.dataset.limit || '8';

      try {
        const params = new URLSearchParams({ type: 'product', q, limit, section_id: sectionId });
        const url = `${window.Shopify.routes.root}search?${params}`;
        const response = await fetch(url);

        if (!response.ok) {
          this._setState('empty');
          return;
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const slides = doc.querySelectorAll('[data-ff-search-slide]');

        if (slides.length === 0) {
          this._updateEmptyState(q);
          this._setState('empty');
          return;
        }

        this._renderSlides(slides, q);
      } catch (e) {
        console.warn('FfSearchResults: fetch error', e);
        this._updateEmptyState(q);
        this._setState('empty');
      }
    }

    /**
     * @param {NodeListOf<Element>} slides
     * @param {string} q
     */
    _renderSlides(slides, q) {
      const list = this.ffSlider && this.ffSlider.querySelector('.splide__list');
      if (!list) return;

      list.innerHTML = '';
      slides.forEach((slide) => list.appendChild(slide.cloneNode(true)));

      // Make panel visible before refresh so Splide can calculate dimensions
      this._setState('results');

      requestAnimationFrame(() => {
        if (this.splideInstance) {
          this.splideInstance.go(0);
          this.splideInstance.refresh();
        }
      });

      // Update show-more link and text
      this._updateShowMoreText(q);
      const searchUrl = `${window.Shopify.routes.root}search?type=product&q=${encodeURIComponent(q)}`;
      const showMoreEl = this.querySelector('[data-ff-search-more]');
      if (showMoreEl) {
        const a = showMoreEl.tagName === 'A'
          ? /** @type {HTMLAnchorElement} */ (showMoreEl)
          : showMoreEl.querySelector('a');
        if (a) a.href = searchUrl;
      }

      if (this.showMoreWrapper) this.showMoreWrapper.style.display = '';
    }

    /** @param {string} q */
    _updateShowMoreText(q) {
      const showMoreEl = this.querySelector('[data-ff-search-more]');
      if (!showMoreEl) return;
      const textEl = showMoreEl.querySelector('p, span, div');
      if (!textEl) return;
      if (!textEl.dataset.template) textEl.dataset.template = textEl.innerHTML;
      textEl.innerHTML = textEl.dataset.template.replace(/\{query\}/g, `"${q}"`);
    }

    /** @param {string} q */
    _updateEmptyState(q) {
      if (!this.overlay) return;
      const descWrapper = this.overlay.querySelector('[data-ff-empty-desc]');
      if (!descWrapper) return;
      const textEl = descWrapper.querySelector('p, span');
      if (!textEl) return;
      if (!textEl.dataset.template) textEl.dataset.template = textEl.innerHTML;
      textEl.innerHTML = textEl.dataset.template.replace(/\{query\}/g, `"${q}"`);
    }

    /** @param {string} state */
    _setState(state) {
      if (!this.overlay) return;
      if (state) {
        this.overlay.dataset.ffSearchState = state;
      } else {
        delete this.overlay.dataset.ffSearchState;
      }
      if (state !== 'results' && this.showMoreWrapper) {
        this.showMoreWrapper.style.display = 'none';
      }
    }
  }

  customElements.define('ff-search-results', FfSearchResults);
}
